import axios from 'axios';
import get from 'lodash-es/get';
import set from 'lodash-es/set';
import unset from 'lodash-es/unset';
import * as Sentry from '@sentry/react';
import { AWSCognitoInstance } from './AWSCognito';
import * as Actions from '../redux/actions';
import { isStaging } from 'services/adminCheck';

const { NODE_ENV } = process.env;

const REACT_APP_AXIOS_BASE_URL = isStaging
  ? 'https://api-stage.averpoint.com'
  : process.env.REACT_APP_AXIOS_BASE_URL;

let dispatch = null;

axios.defaults.baseURL = REACT_APP_AXIOS_BASE_URL;
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const originalRequest = error.config;

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry &&
        dispatch
      ) {
        console.log('[Auth Provider] Retrying with refresh token...');

        originalRequest._retry = true;

        return AWSCognitoInstance.getAccessToken().then((accessToken) => {
          dispatch(Actions.refreshToken(accessToken.data));
          return axios(originalRequest);
        });
      }
    } catch (error) {
      console.error('Error while refreshing token', error);
    }

    // A request the user cancelled (FindArticlesModal aborts its search) is
    // not an error, so it is not reported.
    if (NODE_ENV === 'production' && !axios.isCancel(error)) {
      // Send the error itself, not error.response: the response is a plain
      // object, so Sentry filed every failed request under one "Object
      // captured as exception" issue with no usable stack. The request
      // details go in extra so the endpoint and status are still visible.
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        },
      });
    }

    if (error.response) {
      throw error.response.data || error.response;
    }

    throw error;
  },
);

const addAuthorizationToHeaders = (state, config) => {
  const userToken = get(state, 'user.user.token');
  if (!userToken) {
    return config;
  }

  unset(config, 'headers.authorization');
  set(config, 'headers.Authorization', `Bearer ${userToken}`);

  if (!config.url.includes(process.env.REACT_APP_SOURCE_PROVIDER_LAMBDA)) {
    const impersonateUsername = get(state, 'user.impersonateUsername');
    const impersonateUserId = get(state, 'user.impersonateUserId');

    if (impersonateUsername) {
      set(config, 'headers.impersonate-user-name', impersonateUsername);
      set(config, 'headers.impersonate-user-id', impersonateUserId);
    } else {
      unset(config, 'headers.impersonate-user-name');
      unset(config, 'headers.impersonate-user-id');
    }
  }
  return config;
};

const authorizationProvider = (store) => {
  dispatch = store.dispatch;

  axios.interceptors.request.use((config) => {
    const state = store.getState(store);
    return addAuthorizationToHeaders(state, config);
  });
};

export default authorizationProvider;
